import { getGoogleAdsId } from './runtimeConfig';
import { isNativeApp } from './nativeApp';
import { getMarketingConsent } from './consentStore';
import { logWarn } from './logger';

/**
 * Google Ads Conversion-Tracking & Remarketing (Tag AW-18316473458).
 *
 * Rechtlicher Rahmen (siehe PrivacyPage.js Abschnitt 5.3): Das eigentliche
 * gtag.js-Skript wird ausschließlich nach positiver Einwilligung über den
 * CookieConsentBanner geladen (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1
 * TDDDG). window.dataLayer/gtag existieren zwar bereits vorher (siehe
 * ensureDataLayer()), das ist aber reine lokale Consent-Mode-Buchhaltung —
 * es wird dadurch weder ein Skript nachgeladen noch eine Verbindung zu
 * Google aufgebaut.
 *
 * URLs mit unbekannten Query-Parametern oder Hash werden vor jeder
 * Tag-Initialisierung abgewiesen. Damit haengt der Schutz von Einladungs-,
 * Reset- und OAuth-Daten nicht vom produktspezifischen Verhalten des
 * Google-Tags ab. Der manuelle Pageview enthaelt zusaetzlich nur Origin und
 * Pfad; bekannte Kampagnenparameter bleiben fuer die Attribution lesbar.
 *
 * Bewusst KEIN SPA-Page-View-Tracking bei Routenwechseln (MINOR-11): Nach
 * einer einmal erteilten Einwilligung auf einer Marketing-Seite bleibt das
 * gtag-Script beim SPA-Wechsel in eingeloggte Bereiche (Dashboard,
 * Mietverträge, ...) zwar im Dokument, aber ohne automatischen und ohne
 * manuellen Pageview-Call fließen dort keine weiteren Daten an Google -
 * das ist eine bewusste Entscheidung, kein vergessenes Feature.
 *
 * @module utils/googleAds
 */

/** Guard gegen doppelte Script-Injektion (z.B. mehrfacher Akzeptieren-Klick). */
let scriptInjected = false;

/**
 * Query-Parameter, die Google Ads fuer Kampagnenzuordnung verwenden darf.
 * Unbekannte Parameter werden nicht pauschal als harmlos behandelt: mehrere
 * oeffentliche Routen nutzen Query-Strings fuer Einladungs-, Reset- oder
 * OAuth-Geheimnisse.
 */
const SAFE_MARKETING_QUERY_PARAMS = new Set([
  'dclid',
  'gbraid',
  'gclid',
  'gclsrc',
  'gad_campaignid',
  'gad_source',
  'wbraid',
]);

/**
 * Das Google-Tag darf nur auf URLs initialisiert werden, die keine
 * anwendungsinternen oder unbekannten Daten im Query-String/Hash tragen.
 * UTM-Parameter und die bekannten Google-Ads-Click-IDs bleiben erlaubt.
 *
 * @param {{search?: string, hash?: string}} [location]
 * @returns {boolean}
 */
export const isGoogleAdsLocationSafe = (location = window.location) => {
  if (location.hash) {
    return false;
  }

  const params = new URLSearchParams(location.search || '');
  return Array.from(params.keys()).every((rawName) => {
    const name = rawName.toLowerCase();
    return name.startsWith('utm_') || SAFE_MARKETING_QUERY_PARAMS.has(name);
  });
};

/**
 * Legt window.dataLayer und die klassische gtag()-Funktion an, falls noch
 * nicht geschehen, und setzt beim ALLERERSTEN Aufruf das Google-Consent-
 * Mode-v2-"default"-Signal auf "denied".
 *
 * Diese Reihenfolge ist zwingend: Google wertet die dataLayer-Queue in der
 * Reihenfolge aus, in der sie befüllt wurde. Der default-Consent muss daher
 * VOR jeder Script-Injektion (gtag('js', ...) / gtag('config', ...)) in der
 * Queue liegen — sonst greift für den allerersten Request kein
 * Consent-Signal und Google würde ihn ohne Einschränkung verarbeiten.
 */
const ensureDataLayer = () => {
  const isFirstCall = !window.dataLayer;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // Klassisches gtag.js-Snippet: pusht bewusst das arguments-Objekt (kein
    // Array/Rest-Args) — Google parst intern exakt dieses Format.
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  if (isFirstCall) {
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }
};

/**
 * Prüft, ob Google Ads unter den aktuellen Rahmenbedingungen überhaupt
 * geladen werden darf.
 *
 * Harte Gates:
 *  - !isNativeApp(): Die Play-Store-Data-Safety-Deklaration sagt "Enthält
 *    keine Werbung" / "Kein Werbe-Tracking" zu (siehe
 *    GOOGLE-PLAY-VEROEFFENTLICHUNG.md, Abschnitt Data-Safety) — in der
 *    nativen App darf deshalb unter keinen Umständen ein Ads-Tag geladen
 *    werden, unabhängig von einer eventuellen Einwilligung.
 *  - getGoogleAdsId(): liefert nur bei syntaktisch gültigem Format
 *    (/^AW-\d{6,}$/, geprüft in runtimeConfig.js) einen nicht-leeren Wert.
 *
 * Bewusst KEIN localhost-Gate (anders als das Umami-Snippet in
 * public/index.html): Die E2E-Positivtests müssen den vollständigen
 * Ladepfad auf localhost:3041 durchspielen können. Abgesichert wird
 * stattdessen darüber, dass REACT_APP_GOOGLE_ADS_ID in Test-/Dev-Builds
 * standardmäßig NICHT konfiguriert ist (siehe tests/global-setup.js
 * buildEnv sowie .env/.env.local ohne diesen Key) — ohne ID liefert diese
 * Funktion unabhängig vom Hostnamen false.
 *
 * @returns {boolean}
 */
export const isGoogleAdsEnabled = () => Boolean(getGoogleAdsId()) && !isNativeApp();

/**
 * Injiziert das gtag.js-Skript genau einmal und sendet den initialen
 * page_view. Idempotent (scriptInjected-Guard) und bricht defensiv ab, wenn
 * Google Ads nicht aktiviert ist (siehe isGoogleAdsEnabled()).
 *
 * Vor der Initialisierung greift zusaetzlich isGoogleAdsLocationSafe(). Das
 * ist die eigentliche Sicherheitsgrenze fuer URLs mit Einladungstokens,
 * Reset-Tokens oder OAuth-Daten. send_page_view=false und der bereinigte
 * manuelle Pageview reduzieren den uebermittelten URL-Umfang zusaetzlich,
 * sind aber nicht die einzige Schutzmassnahme.
 */
export const loadGoogleAds = () => {
  if (scriptInjected || !isGoogleAdsEnabled() || !isGoogleAdsLocationSafe()) {
    return;
  }
  ensureDataLayer();

  const adsId = getGoogleAdsId();
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;
  document.head.appendChild(script);
  scriptInjected = true;

  window.gtag('js', new Date());
  window.gtag('config', adsId, { send_page_view: false });
  window.gtag('event', 'page_view', {
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_path: window.location.pathname,
  });
};

/**
 * Aktualisiert das Consent-Mode-Signal auf "granted" und lädt anschließend
 * das Skript. Wird vom CookieConsentBanner beim Klick auf "Akzeptieren"
 * aufgerufen sowie beim Mount, falls in einer früheren Sitzung bereits
 * eingewilligt wurde (sonst würde nach einem Reload nichts mehr laden).
 */
export const grantConsentAndLoad = () => {
  ensureDataLayer();
  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    // Der Banner holt nur eine Einwilligung fuer Google Ads ein. Eine
    // Google-Analytics-Einwilligung ist davon nicht umfasst.
    analytics_storage: 'denied',
  });
  loadGoogleAds();
};

/**
 * Aktualisiert das Consent-Mode-Signal auf "denied" — für die initiale
 * Ablehnung im Banner ebenso wie für den nachträglichen Widerruf in der
 * Datenschutzerklärung (PrivacyPage, Abschnitt 5.3).
 *
 * Kann NICHT ein bereits geladenes Skript aus der laufenden Seite entfernen
 * (aus einer laufenden JS-Umgebung heraus nicht zuverlässig möglich) — die
 * Änderung wirkt daher erst ab dem nächsten Seitenaufbau vollständig
 * (PrivacyPage weist bei einem Widerruf entsprechend ehrlich darauf hin).
 */
export const denyConsent = () => {
  ensureDataLayer();
  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
};

/**
 * Meldet eine Conversion (z.B. erfolgreiche Registrierung) an Google Ads.
 * No-op ohne erteilte Einwilligung, ohne aktivierte Ads-ID oder ohne Label.
 *
 * Es werden bewusst KEINE personenbezogenen Daten mitgegeben (keine E-Mail,
 * kein user_data-Objekt) — "Enhanced Conversions" wird bewusst NICHT
 * aktiviert, um den an Google übermittelten Datenumfang auf das für die
 * reine Erfolgsmessung notwendige Minimum zu beschränken.
 *
 * @param {string} [label] - Conversion-Label aus getGoogleAdsConversionLabel()
 */
export const trackConversion = (label) => {
  if (
    !label ||
    !isGoogleAdsEnabled() ||
    !isGoogleAdsLocationSafe() ||
    getMarketingConsent() !== 'granted'
  ) {
    return;
  }
  try {
    window.gtag('event', 'conversion', {
      send_to: `${getGoogleAdsId()}/${label}`,
    });
  } catch (e) {
    logWarn('[googleAds] Conversion-Tracking fehlgeschlagen.', e);
  }
};
